import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import AdminRoute from '@/components/AdminRoute';
import { TooltipProvider } from '@/components/ui/tooltip';

// Import all admin content components
import BrandsManager from '@/components/dashboard/BrandsManager';
import LocationsManager from '@/components/dashboard/LocationsManager';
import ProductsManager from '@/components/dashboard/ProductsManager';
import CategoryTab from '@/components/dashboard/categories/CategoryTab';
import OrdersManager from '@/components/dashboard/OrdersManager';
import SalesDashboard from '@/components/dashboard/SalesDashboard';
import UsersTable from '@/components/dashboard/users/UsersTable';
import StoreFilter from '@/components/dashboard/users/StoreFilter';
import PointsStatistics from '@/components/dashboard/users/PointsStatistics';
import UserModal from '@/components/dashboard/users/UserModal';
import { useUserManagement } from '@/hooks/useUserManagement';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserCog, FolderTree, Package, BarChart, MapPin, Tag, ShoppingBag, Users, Store, CheckSquare, Plus } from 'lucide-react';
import { ChartContainer } from '@/components/ui/chart';
import { CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Category } from '@/types';
import { Link } from 'react-router-dom';

// Define cores para os gráficos
const COLORS = ['#FF4560', '#00E396', '#FEB019', '#008FFB', '#775DD0'];

interface OrderPoint {
  points_used: number;
  created_at: string;
}

interface DashboardContent {
  path: string;
  title: string;
  component: React.ReactNode;
}

const UnifiedAdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Users management state
  const {
    filteredUsers,
    loading: usersLoading,
    stores,
    selectedStore,
    searchQuery,
    handleStoreChange,
    handleSearchChange,
    fetchUsers,
    activeTab: usersActiveTab,
    setActiveTab: setUsersActiveTab,
  } = useUserManagement();

  // Modal state for user management
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  // User modal handler functions
  const handleCloseAddUserModal = () => {
    setIsAddUserModalOpen(false);
  };

  const handleUserSaved = async () => {
    await fetchUsers();
  };

  // Categories management state
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [categoriesActiveTab, setCategoriesActiveTab] = useState<"categories" | "subcategories">("categories");
  const [categoriesEditingId, setCategoriesEditingId] = useState<string | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Orders active tab state
  const [ordersActiveTab, setOrdersActiveTab] = useState("dashboard");

  // Dashboard statistics state
  const [salesData, setSalesData] = useState<{ month: string; totalPoints: number; orderCount: number }[]>([]);
  const [productStats, setProductStats] = useState<{ name: string; count: number }[]>([]);
  const [summaryStats, setSummaryStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalProducts: 0,
    pendingOrders: 0
  });
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Categories management functions
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);

        const { data: mainCats, error: mainError } = await supabase
          .from('categories')
          .select('*')
          .is('parent_id', null);

        if (mainError) throw mainError;

        const { data: subCats, error: subError } = await supabase
          .from('categories')
          .select('*')
          .not('parent_id', 'is', null);

        if (subError) throw subError;

        const formattedMainCats = mainCats?.map(cat => ({
          id: cat.id,
          name: cat.name,
          image: cat.image || "",
          parentId: cat.parent_id || undefined
        })) || [];

        const formattedSubCats = subCats?.map(cat => ({
          id: cat.id,
          name: cat.name,
          image: cat.image || "",
          parentId: cat.parent_id || undefined
        })) || [];

        setCategories(formattedMainCats);
        setSubcategories(formattedSubCats);
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        toast({
          title: "Erro",
          description: "Erro de comunicação com o servidor",
          variant: "destructive",
        });
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [toast]);

  const onSubmitCategory = async (values: any) => {
    try {
      const categoryData = {
        name: values.name,
        image: values.image || null,
        parent_id: categoriesActiveTab === "subcategories" ? values.parentId : null,
      };

      if (categoriesEditingId) {
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', categoriesEditingId);

        if (error) throw error;
        toast({
          title: "Sucesso",
          description: categoriesActiveTab === "categories" ? "Categoria atualizada com sucesso!" : "Subcategoria atualizada com sucesso!",
        });
      } else {
        const { error } = await supabase
          .from('categories')
          .insert([categoryData]);

        if (error) throw error;
        toast({
          title: "Sucesso",
          description: categoriesActiveTab === "categories" ? "Categoria criada com sucesso!" : "Subcategoria criada com sucesso!",
        });
      }

      resetCategoriesForm();

      // Recarregar categorias
      const { data: mainCats } = await supabase
        .from('categories')
        .select('*')
        .is('parent_id', null);

      const { data: subCats } = await supabase
        .from('categories')
        .select('*')
        .not('parent_id', 'is', null);

      const formattedMainCats = mainCats?.map(cat => ({
        id: cat.id,
        name: cat.name,
        image: cat.image || "",
        parentId: cat.parent_id || undefined
      })) || [];

      const formattedSubCats = subCats?.map(cat => ({
        id: cat.id,
        name: cat.name,
        image: cat.image || "",
        parentId: cat.parent_id || undefined
      })) || [];

      setCategories(formattedMainCats);
      setSubcategories(formattedSubCats);

    } catch (error) {
      console.error("Erro ao guardar categoria:", error);
      toast({
        title: "Erro",
        description: "Erro de comunicação com o servidor",
        variant: "destructive"
      });
    }
  };

  const startCategoriesEditing = (item: Category) => {
    setCategoriesEditingId(item.id);
  };

  const handleCategoriesDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja eliminar esta categoria? Esta ação não pode ser desfeita.")) {
      return;
    }

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Categoria eliminada com sucesso!",
      });

      // Recarregar categorias
      const { data: mainCats } = await supabase
        .from('categories')
        .select('*')
        .is('parent_id', null);

      const { data: subCats } = await supabase
        .from('categories')
        .select('*')
        .not('parent_id', 'is', null);

      const formattedMainCats = mainCats?.map(cat => ({
        id: cat.id,
        name: cat.name,
        image: cat.image || "",
        parentId: cat.parent_id || undefined
      })) || [];

      const formattedSubCats = subCats?.map(cat => ({
        id: cat.id,
        name: cat.name,
        image: cat.image || "",
        parentId: cat.parent_id || undefined
      })) || [];

      setCategories(formattedMainCats);
      setSubcategories(formattedSubCats);

    } catch (error) {
      console.error("Erro ao eliminar categoria:", error);
      toast({
        title: "Erro",
        description: "Erro de comunicação com o servidor",
        variant: "destructive"
      });
    }
  };

  const resetCategoriesForm = () => {
    setCategoriesEditingId(null);
  };

  // Dashboard data fetching
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      fetchDashboardData();
    }
  }, [location.pathname]);

  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);

      // Contagens de utilizadores e produtos
      const [usersRes, productsRes] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('products').select('*', { count: 'exact', head: true }),
      ]);

      if (usersRes.error || productsRes.error) throw new Error('Erro ao buscar contagens.');

      // Contagem de pedidos
      const { count: totalOrders, error: countError } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('id, status');

      if (ordersError) throw ordersError;

      const pendingOrders = ordersData ? ordersData.filter(o => o.status === 'pending').length : 0;

      setSummaryStats({
        totalUsers: usersRes.count || 0,
        totalProducts: productsRes.count || 0,
        totalOrders: totalOrders || 0,
        pendingOrders
      });

      // Vendas mensais (últimos 6)
      const { data: orders } = await supabase
        .from('orders')
        .select('points_used, created_at');

      if (orders) {
        const orderPoints: OrderPoint[] = orders.map(order => ({
          points_used: order.points_used,
          created_at: order.created_at
        }));
        setSalesData(processOrdersData(orderPoints));
      }

      // Produtos por categoria
      const { data: cats } = await supabase
        .from('products')
        .select('category_id, categories:categories(name)');

      if (cats) {
        const categoryData = processCategoryData(cats);
        setProductStats(categoryData);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar dados';
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setDashboardLoading(false);
    }
  };

  const processOrdersData = (orders: OrderPoint[]) => {
    const byMonth: Record<string, { month: string; totalPoints: number; orderCount: number }> = {};
    orders.forEach(o => {
      const d = new Date(o.created_at);
      const key = `${d.getMonth() + 1}/${d.getFullYear()}`;
      if (!byMonth[key]) byMonth[key] = { month: key, totalPoints: 0, orderCount: 0 };
      byMonth[key].totalPoints += o.points_used;
      byMonth[key].orderCount += 1;
    });
    return Object.values(byMonth).slice(-6);
  };

  const processCategoryData = (cats: { categories?: { name?: string } }[]) => {
    const counts: Record<string, number> = {};
    cats.forEach(c => {
      const name = c.categories?.name || 'Sem categoria';
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({ name, count }));
  };

  // Filter users by status
  const activeUsers = filteredUsers.filter(user => user.status === 'active');
  const inactiveUsers = filteredUsers.filter(user => user.status === 'inactive');

  // Define dashboard content based on current route
  const dashboardContent: DashboardContent[] = useMemo(() => [
    {
      path: '/dashboard',
      title: 'Dashboard Principal',
      component: (
        <div className="container mx-auto">
          {/* Cards resumo */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            <Link to="/users-dashboard" className="block">
              <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400 flex items-center">
                    <Users size={16} className="mr-2" />
                    Usuários
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summaryStats.totalUsers}</div>
                  <p className="text-sm text-gray-400 mt-1">registados</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/orders-dashboard" className="block">
              <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400 flex items-center">
                    <CheckSquare size={16} className="mr-2" />
                    Pedidos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summaryStats.totalOrders}</div>
                  <p className="text-sm text-gray-400 mt-1">
                    <span className="text-yellow-400">{summaryStats.pendingOrders} pendentes</span>
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/products-dashboard" className="block">
              <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400 flex items-center">
                    <ShoppingBag size={16} className="mr-2" />
                    Produtos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summaryStats.totalProducts}</div>
                  <p className="text-sm text-gray-400 mt-1">registados</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/category-dashboard" className="block">
              <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardDescription className="text-gray-400 flex items-center">
                    <FolderTree size={16} className="mr-2" />
                    Categorias
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{productStats.length}</div>
                  <p className="text-sm text-gray-400 mt-1">registadas</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Área rápida */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Link to="/products-dashboard">
              <Card className="bg-brand-gray border-none text-white hover:bg-brand-gray/90 transition-colors h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package size={20} />
                    Gerenciar Produtos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">Adicione, edite e remova produtos</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/orders-dashboard">
              <Card className="bg-brand-gray border-none text-white hover:bg-brand-gray/90 transition-colors h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart size={20} />
                    Painel de Vendas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">Estatísticas e gestão de encomendas</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/users-dashboard">
              <Card className="bg-brand-gray border-none text-white hover:bg-brand-gray/90 transition-colors h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users size={20} />
                    Gerenciar Usuários
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">Visualize pontos e contas</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Vendas Mensais */}
            <Card className="bg-brand-gray border-none text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart size={20} />
                  Vendas Mensais
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Últimos 6 meses
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardLoading ? (
                  <div className="flex justify-center h-80 items-center">
                    <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red" />
                  </div>
                ) : (
                  <div className="h-80">
                    <ChartContainer
                      config={{
                        points: { label: "Pontos", color: "#FF4560" },
                        orders: { label: "Pedidos", color: "#00E396" }
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={salesData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                          <XAxis dataKey="month" stroke="#888" />
                          <YAxis stroke="#888" />
                          <Tooltip contentStyle={{ backgroundColor: '#222', border: 'none', borderRadius: '8px' }} itemStyle={{ color: '#fff' }} />
                          <Line type="monotone" dataKey="totalPoints" name="Pontos" stroke="#FF4560" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                          <Line type="monotone" dataKey="orderCount" name="Pedidos" stroke="#00E396" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Produtos por Categoria */}
            <Card className="bg-brand-gray border-none text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderTree size={20} />
                  Produtos por Categoria
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Distribuição
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardLoading ? (
                  <div className="flex justify-center h-80 items-center">
                    <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red" />
                  </div>
                ) : productStats.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={productStats} cx="50%" cy="50%" outerRadius={120} innerRadius={60} dataKey="count" label={({ name, percent }) => `${name} (${(percent*100).toFixed(0)}%)`}>
                          {productStats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#222', border: 'none', borderRadius: '8px' }} itemStyle={{ color: '#fff' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-80 text-gray-500">
                    Sem dados para exibir
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },
    {
      path: '/users-dashboard',
      title: 'Gestão de Utilizadores',
      component: (
        <>
          <CardHeader>
            <CardTitle className="flex items-center">
              <UserCog className="mr-2" />
              Utilizadores do Sistema
            </CardTitle>
            <CardDescription className="text-gray-400">
              Gira todos os utilizadores da plataforma e visualiza pontos por loja
            </CardDescription>
          </CardHeader>

          <Card className="bg-brand-gray border-none text-white mb-8">
            <CardContent className="p-6">
              <StoreFilter
                stores={stores}
                selectedStore={selectedStore}
                onStoreChange={handleStoreChange}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
              />

              <PointsStatistics users={filteredUsers} />

              {/* Botão para adicionar utilizador */}
              <Button
                onClick={() => setIsAddUserModalOpen(true)}
                disabled={usersLoading}
                className="bg-brand-red hover:bg-brand-red/90 text-white mb-6"
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Utilizador
              </Button>

              <div className="mt-8">
                <Tabs value={usersActiveTab} onValueChange={(value: string) => setUsersActiveTab(value as 'active' | 'inactive')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="active">
                      Utilizadores Activos ({activeUsers.length})
                    </TabsTrigger>
                    <TabsTrigger value="inactive">
                      Utilizadores Inactivos ({inactiveUsers.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="active">
                    <UsersTable
                      users={activeUsers}
                      loading={usersLoading}
                      fetchUsers={fetchUsers}
                      stores={stores}
                    />
                  </TabsContent>

                  <TabsContent value="inactive">
                    <UsersTable
                      users={inactiveUsers}
                      loading={usersLoading}
                      fetchUsers={fetchUsers}
                      stores={stores}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </CardContent>
          </Card>

          {/* Modal para adicionar utilizador */}
          <UserModal
            isOpen={isAddUserModalOpen}
            onClose={handleCloseAddUserModal}
            editingUser={null}
            onUserSaved={handleUserSaved}
          />
        </>
      )
    },
    {
      path: '/products-dashboard',
      title: 'Gestão de Produtos',
      component: <ProductsManager />
    },
    {
      path: '/category-dashboard',
      title: 'Gestão de Categorias',
      component: (
        <Card className="bg-brand-gray border-none text-white mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FolderTree className="mr-2" />
              Categorias e Subcategorias
            </CardTitle>
            <CardDescription className="text-gray-400">
              Faça a gestão das categorias e subcategorias de produtos
            </CardDescription>
          </CardHeader>
          <CardContent>
            {categoriesLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-brand-red"></div>
              </div>
            ) : (
              <Tabs value={categoriesActiveTab} onValueChange={(value) => setCategoriesActiveTab(value as "categories" | "subcategories")} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="categories">Categorias</TabsTrigger>
                  <TabsTrigger value="subcategories">Subcategorias</TabsTrigger>
                </TabsList>

                <TabsContent value="categories">
                  <CategoryTab
                    activeTab="categories"
                    categories={categories}
                    subcategories={subcategories}
                    editingId={categoriesEditingId}
                    onSubmit={onSubmitCategory}
                    onEdit={startCategoriesEditing}
                    onDelete={handleCategoriesDelete}
                    resetForm={resetCategoriesForm}
                  />
                </TabsContent>

                <TabsContent value="subcategories">
                  <CategoryTab
                    activeTab="subcategories"
                    categories={categories}
                    subcategories={subcategories}
                    editingId={categoriesEditingId}
                    onSubmit={onSubmitCategory}
                    onEdit={startCategoriesEditing}
                    onDelete={handleCategoriesDelete}
                    resetForm={resetCategoriesForm}
                  />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      )
    },
    {
      path: '/orders-dashboard',
      title: 'Painel de Vendas',
      component: (
        <Tabs
          value={ordersActiveTab}
          onValueChange={setOrdersActiveTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart size={16} />
              <span>Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Package size={16} />
              <span>Pedidos</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <Card className="bg-brand-gray border-none text-white p-6">
              <SalesDashboard />
            </Card>
          </TabsContent>

          <TabsContent value="orders">
            <Card className="bg-brand-gray border-none text-white p-6">
              <OrdersManager />
            </Card>
          </TabsContent>
        </Tabs>
      )
    },
    {
      path: '/locations-dashboard',
      title: 'Gestão de Locais',
      component: (
        <Card className="bg-brand-gray border-none text-white">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Store className="mr-2" />
              Gestão de Locais
            </CardTitle>
            <CardDescription className="text-gray-400">
              Adicione, edite e elimine os locais (lojas físicas)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LocationsManager />
          </CardContent>
        </Card>
      )
    },
    {
      path: '/brands-dashboard',
      title: 'Gestão de Marcas',
      component: (
        <Card className="bg-brand-gray border-none text-white">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Tag className="mr-2" />
              Gestão de Marcas
            </CardTitle>
            <CardDescription className="text-gray-400">
              Adicione, edite e elimine as marcas dos produtos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BrandsManager />
          </CardContent>
        </Card>
      )
    },
    {
      path: '/stores-dashboard',
      title: 'Gestão de Lojas',
      component: (
        <Card className="bg-brand-gray border-none text-white">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Store className="mr-2" />
              Gestão de Lojas
            </CardTitle>
            <CardDescription className="text-gray-400">
              Adicione, edite e elimine as lojas da plataforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-gray-400">Gestão de lojas em desenvolvimento</p>
            </div>
          </CardContent>
        </Card>
      )
    }
  ], [
    filteredUsers, usersLoading, stores, selectedStore, searchQuery,
    activeUsers, inactiveUsers, usersActiveTab, categories, subcategories,
    categoriesActiveTab, categoriesEditingId, categoriesLoading, ordersActiveTab
  ]);

  // Find current content based on pathname
  const currentContent = dashboardContent.find(content => content.path === location.pathname);

  // Default to main dashboard if route not found
  const contentToRender = currentContent || dashboardContent[0];

  return (
    <AdminRoute>
      <TooltipProvider>
        <AdminLayout title={contentToRender.title}>
          {contentToRender.component}
        </AdminLayout>
      </TooltipProvider>
    </AdminRoute>
  );
};

export default UnifiedAdminDashboard;